import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { BPProfilesService, BPProfilesViewCode } from '../../bp-profiles.service';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { GetRowIdParams, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { IColDef } from '../../../../shared/report/report-grid.service';
import { CellRendererComp } from '../../../../shared/ag-grid-components/ga-ag-grid.component';
import { IAgButtonDropdownRendererParams } from '../../../../shared/ag-grid-components/ag-button-dropdown/ag-button-dropdown-renderer.component';
import { BPProfilesOEMService } from './bp-profiles-oem.service';
import { BPProfilesOEMDataService, IOEMDistributor, IOEMFilter, IOEMRelation, OEMFilterCode, OEMTierStatus } from './bp-profiles-oem-data.service';
import { util } from '../../../../shared/util';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { IRichTextGrid, IRichTextRendererParams, RichTextType } from '../../../../shared/ag-grid-components/ag-rich-text/ag-rich-text-renderer.component';
import * as _ from 'underscore';
import { IAgContactInfoRendererParams } from '../../../../shared/ag-grid-components/ag-contact-info/ag-contact-info-renderer.component';
import { IRoleContact } from '../../../webshop/info/webshop-info-data.service';
import { DialogBusyButtonFn, DialogSize, GaMessageService, IDialog, IDialogButton } from '../../../../shared/message/ga-message.service';
import { IAddDistributorModel, OEMAddDistributorDialogComponent } from './dialogs/oem-add-distributor-dialog.component';
import { IOEMChangeRankingModel, OEMChangeRankingDialogComponent } from './dialogs/oem-change-ranking-dialog.component';
import { IOEMTierStatusDialogModel, OEMTierStatusDialogComponent, TierStatusDialogMode } from './dialogs/oem-tier-status-dialog.component';
import { ICompanyCountry } from '../../../../../type_definitions/datatypes';
import { StateService, TransitionPromise } from '@uirouter/angular';
import { STATE_BPPROFILES_OEM_DETAIL } from '../../../../shared/app-states';
import { Subscription } from 'rxjs';
import { BUTTON_CANCEL, BUTTON_DELETE } from '../../../../shared/message/dialogs/default-form-dialog.component';
import { GaAgGridService } from '../../../../shared/ag-grid-components/ga-ag-grid.service';
import { GRID_BP_OEM_OVERVIEW } from '@app/shared/app-grids';

enum GridColumnKey {
  OPTIONS = 'options',
  OEM = 'oem',
  RANKING = 'acslRanking',
  TIER_STATUS = 'tierStatus',
  DISTRIBUTOR = 'distributor',
  DIRECT_PROCESS = 'directProcess',
  OEM_ORDER_CURRENCY = 'oemOrderCurrency',
  DEFAULT_OFT = 'defaultOFT',
  CONTACTS = 'contacts'
}

const gridHeightOffset:string = '30px;.ui-layout-north;.ui-layout-resizer-north;.oem-header;.fixed-to-top-container;.new-oem-button';

@Component({
  selector: 'bp-profiles-oem-overview',
  template: `
    <div class="row oem-header">
      <div class="col-md-4 header-info">
        <p class="text-primary" *ngIf="isACSLUser"><i class="fa fa-info-circle"></i> <a [href]="bpOemCoverageUrl | safe: 'url'" target="_blank" translate>BPPROFILES.OEM.INFO.COVERAGE</a></p>
        <p class="text-primary" *ngIf="isACSLUser"><faq-popup faqCode="OEM_BP_BULK_UPDATE"><i class="fa fa-info-circle"></i> <span translate>BPPROFILES.OEM.INFO.BULK_UPDATE</span></faq-popup></p>
      </div>

      <div class="col-md-8">
        <div class="options float-end">
          <label class="form-check form-check-inline" *ngFor="let filter of oemFilters">
            <input class="form-check-input oem-filter-radio" type="radio" [ngModel]="selectedFilter" (ngModelChange)="onSelectedFilterChange($event)" [value]="filter.code" [disabled]="loading">
            <span class="form-check-label">{{filter.label}}</span>
          </label>
        </div>
      </div>
    </div>

    <div class="new-oem-button" *ngIf="hasWritePermission">
      <button class="btn btn-primary" translate [translateParams]="{ label: 'OEM' }" (click)="addNewOEM()">GA.BUTTONS.NEW_LABEL</button>
    </div>

    <ga-ag-grid [gridId]="gridId" [gridOptions]="gridOptions" [gridInit]="gridInit" [fillHeight]="'${gridHeightOffset}'"></ga-ag-grid>
  `
})
export class BPProfilesOEMOverviewComponent implements OnInit, OnDestroy {
  @Input() public filter:OEMFilterCode;

  public selectedFilter:OEMFilterCode;
  public isACSLUser:boolean;
  public bpOemCoverageUrl:string;
  public oemFilters:IOEMFilter[];
  public hasWritePermission:boolean;
  public gridOptions:GridOptions;
  public gridId:string = GRID_BP_OEM_OVERVIEW;
  public gridInit:boolean;

  private _loading:boolean;
  private onActiveProfileChangeSubscription:Subscription;

  constructor(
    private translationService:TranslationService,
    private authService:AuthService,
    private bpProfilesService:BPProfilesService,
    private bpProfilesOEMService:BPProfilesOEMService,
    private bpProfilesOEMDataService:BPProfilesOEMDataService,
    private formattingService:FormattingService,
    private messageService:GaMessageService,
    private stateService:StateService,
    private gaAgGridService:GaAgGridService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.hasWritePermission = this.authService.viewWrite(BPProfilesViewCode.OEM);
    this.isACSLUser = this.authService.checkAcslUser();
    this.bpOemCoverageUrl = `/ga2/#/reports/195?autoload=true&filters=Denname%09${this.bpProfilesService.activeProfile?.companyCountryId}&filters=Active_oem%09`;
    this.oemFilters = this.bpProfilesOEMDataService.getOEMFilters();
    this.selectedFilter = this.filter || OEMFilterCode.COVERED;
    // initialize grid
    this.gridInitialize();

    // register listener
    this.listen();
  }

  private listen():void {
    this.onActiveProfileChangeSubscription = this.bpProfilesService.onActiveProfileChange.subscribe(() => {
      this.bpProfilesOEMService.oemRelations = undefined;
      this.loadData();
    });
  }

  public get loading():boolean {
    return this._loading;
  }

  public set loading(isLoading:boolean) {
    this._loading = isLoading;
    this.gaAgGridService.setGridLoading(this.gridId, isLoading);
  }

  public onSelectedFilterChange(selectedFilter:OEMFilterCode):void {
    this.selectedFilter = selectedFilter;

    if (this.bpProfilesOEMService.oemRelations?.length) {
      const data:IOEMRelation[] = _.clone(this.bpProfilesOEMService.oemRelations);
      const processedData:IOEMRelation[] = _.filter(data, (oem:IOEMRelation) => {
        switch (selectedFilter) {
          case OEMFilterCode.COVERED:
            return !oem.tierStatus || oem.tierStatus.id !== OEMTierStatus.NOT_COVERED;
          case OEMFilterCode.NON_COVERED:
            return oem.tierStatus && oem.tierStatus.id === OEMTierStatus.NOT_COVERED;
          case OEMFilterCode.ACTIVE:
            return oem.hasActiveWebshop;
          case OEMFilterCode.INFO_MISSING:
            return oem.basicInfoMissing;
        }
      });
      this.gaAgGridService.setRowData(this.gridId, processedData, true);
    }
  }

  private refreshRowGridData():void {
    this.onSelectedFilterChange(this.selectedFilter);
  }

  private loadData():void {
    this.loading = true;
    this.bpProfilesOEMDataService.getOEMRelations(this.bpProfilesService.activeProfile?.id)
      .then((oemRelations:IOEMRelation[]) => {
        this.bpProfilesOEMService.oemRelations = oemRelations;
        this.onSelectedFilterChange(this.selectedFilter);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private getColDefs():IColDef[] {
    const colDefs:IColDef[] = [
      {
        field: GridColumnKey.OPTIONS,
        headerName: '',
        sortable: false,
        pinned: 'left',
        width: 46,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        suppressColumnsToolPanel: true,
        cellClass: 'cell-actions text-center ag-button-dropdown-renderer',
        cellRenderer: CellRendererComp.BUTTON_DROPDOWN_RENDERER,
        cellRendererParams: <Partial<IAgButtonDropdownRendererParams>>{
          dropdownMenus: [
            {
              label: 'BPPROFILES.OEM.OPTIONS.SHOW_DETAIL',
              onClick: this.onShowDetail,
              iconClass: 'fa-eye'
            },
            {
              label: 'GA.BUTTONS.EDIT',
              onClick: this.onEditOEM,
              iconClass: 'fa-pencil',
              isHidden: !this.hasWritePermission
            },
            {
              label: 'BPPROFILES.OEM.OPTIONS.CHANGE_TIER_STATUS',
              onClick: this.onChangeTierStatus,
              iconClass: 'fa-pencil',
              isHidden: !this.hasWritePermission
            },
            {
              label: 'BPPROFILES.OEM.OPTIONS.CHANGE_ACSL_RANKING',
              onClick: this.onChangeACSLRanking,
              iconClass: 'fa-pencil',
              isHidden: !this.hasWritePermission
            },
            {
              label: 'GA.BUTTONS.ADD_LABEL',
              onClick: this.onAddDistributor,
              iconClass: 'fa-plus-circle',
              isHidden: !this.hasWritePermission,
              translationParams: { label: 'distributor' }
            },
            {
              label: 'BPPROFILES.OEM.OPTIONS.SET_PREFERRED_DISTRIBUTOR',
              onClick: this.onSetPreferredDistributor,
              iconClass: 'fa-star',
              hideExpression: (params:IAgButtonDropdownRendererParams, index?:number) => !this.hasWritePermission || (!params.data.distributors.length || (<IOEMDistributor>params.data.distributors?.[ index ])?.preferred)
            },
            {
              label: 'GA.BUTTONS.DELETE',
              onClick: this.onDeleteDistributor,
              iconClass: 'fa-trash-o',
              isHidden: !this.hasWritePermission
            }
          ],
          repeatCount: (params:IAgButtonDropdownRendererParams) => {
            if (params.data.tierStatus?.id === OEMTierStatus.DIRECT) {
              // direct can have distributors as well
              return 1 + (params.data.distributors?.length || 0);
            } else if (params.data.tierStatus?.id === OEMTierStatus.DISTRIBUTOR) {
              return params.data.distributors?.length || 1;
            }
            return 1;
          }
        }
      },
      {
        field: GridColumnKey.OEM,
        headerName: 'BPPROFILES.OEM.TEXT',
        cellClass: 'multi-line-cell',
        sortable: true,
        valueGetter: 'data.oem && data.oem.name',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{ label: params.value }],
          showTooltip: true
        }
      },
      {
        field: GridColumnKey.RANKING,
        headerName: 'BPPROFILES.OEM.GRID.ACSL_RANKING',
        sortable: true,
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{ label: params.value, textClass: 'badge bg-primary' }],
          showTooltip: true
        },
        cellClass: 'text-center',
        valueGetter: 'data.ranking && data.ranking.value'
      },
      {
        field: GridColumnKey.TIER_STATUS,
        headerName: 'BPPROFILES.OEM.GRID.TIER_STATUS',
        sortable: true,
        valueGetter: 'data.tierStatus && data.tierStatus.value',
        valueFormatter: (params:ValueFormatterParams):string => this.formattingService.formatOEMType(params.value),
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{ label: params.valueFormatted, textClass: `badge ${this.bpProfilesOEMDataService.getTierStatusOption(params.data.tierStatus)?.labelClass}` }],
          showTooltip: true
        }
      },
      {
        field: GridColumnKey.DISTRIBUTOR,
        headerName: 'BPPROFILES.OEM.GRID.DISTRIBUTOR',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellClass: 'multi-line-cell',
        autoHeight: true,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => {
            const oemRelation:IOEMRelation = params.data;
            const distributors:IRichTextGrid[] = [];
            // if tier status is DIRECT but there are distributors, first row should always be empty distributor
            if (oemRelation.tierStatus?.id === OEMTierStatus.DIRECT && oemRelation.distributors?.length) {
              distributors.push({ label: '' });
            }
            _.each(params.data.distributors, (distributor:IOEMDistributor) => {
              distributors.push({
                label: this.formattingService.formatCompanyCountry(distributor.companyCountry),
                iconClass: distributor.preferred ? 'fa-star favorite' : '',
                iconPosition: 'right'
              });
            });
            return distributors;
          },
          showTooltip: true
        }
      },
      {
        field: GridColumnKey.DIRECT_PROCESS,
        headerName: 'BPPROFILES.OEM.GRID.DIRECT_PROCESS',
        cellClass: 'text-center',
        sortable: true,
        valueGetter: 'data.directOrderProcess && data.directOrderProcess.value',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{ label: params.value }],
          showTooltip: true
        }
      },
      {
        field: GridColumnKey.OEM_ORDER_CURRENCY,
        headerName: this.translationService.instant('BPPROFILES.OEM.GRID.OEM_ORDER_CURRENCY'),
        cellClass: 'text-center',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => {
            if (params.data.distributors?.length) {
              const oemRelation:IOEMRelation = params.data;
              const oemPaymentCurrencies:IRichTextGrid[] = [];
              // if tier status is DIRECT but there are distributors, first row should always be OEM relation
              if (oemRelation.tierStatus?.id === OEMTierStatus.DIRECT && oemRelation.distributors?.length) {
                oemPaymentCurrencies.push({ label: this.formattingService.formatCurrencyS(oemRelation.paymentCurrency) });
              }
              _.each(params.data.distributors, (distributor:IOEMDistributor) => {
                oemPaymentCurrencies.push({ label: this.formattingService.formatCurrencyS(distributor.paymentCurrency) });
              });
              return oemPaymentCurrencies;
            } else {
              return [{ label: this.formattingService.formatCurrencyS(params.data.paymentCurrency) }];
            }
          },
          showTooltip: true
        }
      },
      {
        field: GridColumnKey.DEFAULT_OFT,
        headerName: 'BPPROFILES.OEM.GRID.DEFAULT_OFT',
        cellClass: 'text-center',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => {
            if (params.data.distributors?.length) {
              const oemRelation:IOEMRelation = params.data;
              const oft:IRichTextGrid[] = [];
              // if tier status is DIRECT but there are distributors, first row should always be OEM relation
              if (oemRelation.tierStatus?.id === OEMTierStatus.DIRECT && oemRelation.distributors?.length) {
                oft.push({ label: oemRelation.oft || '' });
              }
              _.each(params.data.distributors, (distributor:IOEMDistributor) => {
                oft.push({ label: distributor.oft || '' });
              });
              return oft;
            } else {
              return [{ label: params.data.oft || '' }];
            }
          },
          showTooltip: true
        }
      },
      {
        field: GridColumnKey.CONTACTS,
        headerName: 'BPPROFILES.OEM.GRID.CONTACTS',
        cellClass: 'multi-line-cell',
        cellRenderer: CellRendererComp.CONTACT_INFO_RENDERER,
        cellRendererParams: <IAgContactInfoRendererParams>{
          getRoleContact: (params:IAgContactInfoRendererParams):IRoleContact[] => {
            let contacts:IRoleContact[] = params.data.contacts || [];

            _.each(params.data.distributors, (distributor:IOEMDistributor) => {
              contacts = contacts.concat(distributor.contacts);
            });

            return contacts;
          },
          showTooltip: true
        }
      }
    ];

    _.forEach(colDefs, (colDef:IColDef) => {
      colDef.headerName = this.translationService.instant(colDef.headerName);

      if (colDef.field !== GridColumnKey.OPTIONS) {
        colDef.resizable = true;
      }
    });

    return colDefs;
  }

  private gridInitialize():void {
    this.gridOptions = {
      columnDefs: this.getColDefs(),
      onGridReady: ():void => {
        this.loadData();
      },
      onFirstDataRendered: ():void => {
        this.gaAgGridService.sizeColumnsToFit(this.gridId);
        this.highlightSelectedRow();
      },
      tooltipShowDelay: 0,
      suppressMovableColumns: true,
      getRowId: (row:GetRowIdParams<IOEMRelation>) => row.data.id?.toString()
    };

    this.gridInit = true;
  }

  private findOEMById(oemId:number):IOEMRelation {
    const oemRelations:IOEMRelation[] = this.bpProfilesOEMService.oemRelations;
    const oemRelationIndex:number = _.findIndex(oemRelations, { id: oemId });
    return oemRelations[oemRelationIndex];
  }

  private getDistributorIdFromGrid(oemRelation:IOEMRelation, index?:number):number {
    return oemRelation.tierStatus?.id === OEMTierStatus.DIRECT? oemRelation.distributors?.[index-1]?.id : oemRelation.distributors?.[index]?.id;
  }

  private goToOEMDetail(oemRelationId:number, distributorId?:number):TransitionPromise {
    this.bpProfilesOEMService.selectedOEMRelationId = oemRelationId;
    return this.stateService.go(STATE_BPPROFILES_OEM_DETAIL,{ oemId: this.bpProfilesOEMService.toStateParam(oemRelationId, distributorId) });
  }

  private highlightSelectedRow():void {
    if (this.bpProfilesOEMService.selectedOEMRelationId) {
      // add timeout to wait until ag-grid is fully rendered
      setTimeout(() => {
        // highlight grid to selected row when coming from detail
        this.gaAgGridService.highlightGridRow(this.gridId, this.bpProfilesOEMService.selectedOEMRelationId);
        // clear selected oem relation id after scrolling
        this.bpProfilesOEMService.selectedOEMRelationId = undefined;
      });
    }
  }

  public addNewOEM():void {
    this.onChangeTierStatus(undefined, undefined, TierStatusDialogMode.NEW, this.translationService.instant('GA.BUTTONS.NEW_LABEL', { label: 'OEM' }));
  }

  private onShowDetail = (params:IAgButtonDropdownRendererParams, index?:number):void => {
    const oemRelation:IOEMRelation = params.data;
    this.goToOEMDetail(oemRelation.id, this.getDistributorIdFromGrid(oemRelation, index));
  };

  private onEditOEM = (params:IAgButtonDropdownRendererParams, index?:number):void => {
    const oemRelation:IOEMRelation = params.data;

    // 1) if tier status is not defined then user needs to select tier status first
    // 2) If tier status is distributor but there are no distributor selected then user needs to select distributor or change tier status
    // 3) Go to detail page
    if (!oemRelation.tierStatus) {
      this.onChangeTierStatus(params, index, TierStatusDialogMode.EDIT, 'BPPROFILES.OEM.SELECT_TIER_STATUS');
    } else if (oemRelation.tierStatus?.id === OEMTierStatus.DISTRIBUTOR && !oemRelation.distributors?.[index]?.id) {
      this.onChangeTierStatus(params, index, TierStatusDialogMode.EDIT, 'BPPROFILES.OEM.ADD_DISTRIBUTOR_FIRST');
    } else {
      this.goToOEMDetail(oemRelation.id, this.getDistributorIdFromGrid(oemRelation, index))
        .then(() => {
          // signal page in editing mode immediately after state change
          this.bpProfilesService.editingMode = true;
        });
    }
  };

  private onChangeTierStatus = (params:IAgButtonDropdownRendererParams, _index?:number, mode?:TierStatusDialogMode, title?:string):void => {
    let oemRelation:IOEMRelation = params?.data || {};

    const dialog:IDialog = this.messageService.showDialog({
      component: OEMTierStatusDialogComponent,
      wrapperComponent: false,
      inputs: {
        selectedOEM: oemRelation,
        title: this.translationService.instant(title || 'BPPROFILES.OEM.CHANGE_TIER_STATUS.TITLE', { oemName: oemRelation.oem?.name }),
        adding: mode === TierStatusDialogMode.NEW,
        entity: 'BPPROFILES.OEM.OEM.LABEL'
      },
      outputs: {
        onSave: (model:IOEMTierStatusDialogModel):void => {
          const profileId:number = this.bpProfilesService.activeProfile?.id;

          // if oem already existed and dialog mode is 'NEW' then do nothing
          const existedOEM:IOEMRelation = _.find(this.bpProfilesOEMService.oemRelations, (relation:IOEMRelation) => relation?.oem?.id === model.oem?.id);
          if (existedOEM && existedOEM?.tierStatus?.id !== OEMTierStatus.NOT_COVERED && mode === TierStatusDialogMode.NEW) {
            this.messageService.showMessageDialog(
              this.translationService.instant('BPPROFILES.OEM.EXIST_OEM_TITLE'),
              this.translationService.instant('BPPROFILES.OEM.EXIST_OEM_MESSAGE', { oemName: model?.oem?.name }),
              undefined,
              undefined,
              undefined,
              DialogSize.MD
            );
            return dialog.hide();
          } else {
            // replace oem relation variable data with existed oem
            oemRelation = _.extend(oemRelation, existedOEM);
          }

          // updated data based on dialog model
          oemRelation.comments = model.comments;
          oemRelation.tierStatus = model.tierStatus;

          let promise:Promise<IOEMRelation>;
          if (oemRelation.id) {
            promise = this.bpProfilesOEMDataService.updateOEMRelation(profileId, oemRelation);
          } else {
            oemRelation.oem = model.oem;
            promise = this.bpProfilesOEMDataService.addOEMRelation(profileId, oemRelation);
          }

          dialog.content.inputs.saving = true;
          promise
            .then((updatedOemRelation:IOEMRelation) => {
              // code below will be set when dialog mode is 'NEW'
              if (!oemRelation.id) {
                // replaceo em relation with new oem added from server
                oemRelation = updatedOemRelation;
                // 'distributors' always undefined from server
                oemRelation.distributors = [];
                // add new oem to stored data
                this.bpProfilesOEMService.oemRelations = this.bpProfilesOEMService.oemRelations.concat([ oemRelation ]);
              }

              const oemData:IOEMRelation = this.findOEMById(oemRelation.id);
              oemData.tierStatus = updatedOemRelation.tierStatus;
              oemData.comments = updatedOemRelation.comments;

              const goToDetail = (distributorId?:number):void => {
                if (title) {
                  this.goToOEMDetail(oemRelation.id, distributorId);
                }
              };

              // if tier status selection is distributor then:
              // 1) mark as preferred if distributor selection is existed in current OEM distributor and currently is not preferred distributor
              // 2) Add new distributor if distributor selection is not existed in current oem relation distributors
              if (model.tierStatus?.id === OEMTierStatus.DISTRIBUTOR) {
                const currentDistributorIndex:number = _.findIndex(oemRelation.distributors, (distributor:IOEMDistributor) => distributor.companyCountry?.id === model.distributor?.id);
                const currentDistributor:IOEMDistributor = oemRelation.distributors?.[currentDistributorIndex];

                if (currentDistributor && !currentDistributor.preferred) {
                  currentDistributor.preferred = true;
                  this.bpProfilesOEMDataService.updateDistributor(profileId, oemRelation.id, currentDistributor)
                    .then((updatedDistributor:IOEMDistributor) => {
                      this.onUpdatePreferredDistributor(updatedOemRelation, currentDistributorIndex, updatedDistributor);
                      goToDetail(updatedDistributor?.id);
                      dialog.hide();
                    });
                } else if (!currentDistributor) {
                  this.bpProfilesOEMDataService.addNewDistributor(profileId, oemRelation.id, true, <ICompanyCountry>model.distributor)
                    .then((newDistributor:IOEMDistributor) => {
                      this.onNewDistributorAdded(updatedOemRelation, newDistributor);
                      goToDetail(newDistributor?.id);
                      dialog.hide();
                    });
                }
              } else {
                this.refreshRowGridData();
                const preferredDistributorId:number = _.findWhere(oemData.distributors, { preferred: true })?.id;
                goToDetail(preferredDistributorId);
                dialog.hide();
              }
            })
            .catch(() => {
              dialog.content.inputs.saving = false;
            });
        },
        onCancel: () => dialog.hide()
      },
      size: DialogSize.MD
    });
  };

  private onChangeACSLRanking = (params:IAgButtonDropdownRendererParams):void => {
    const oemRelation:IOEMRelation = params.data;
    const dialog:IDialog = this.messageService.showDialog({
      component: OEMChangeRankingDialogComponent,
      wrapperComponent: false,
      inputs: {
        model: {
          ranking: oemRelation?.ranking
        },
        title: this.translationService.instant('BPPROFILES.OEM.CHANGE_RANKING.TITLE', { oemName: oemRelation?.oem?.name })
      },
      outputs: {
        onSave: (model:IOEMChangeRankingModel):void => {
          dialog.content.inputs.saving = true;
          this.bpProfilesOEMDataService.updateOEMRelation(this.bpProfilesService.activeProfile?.id, _.extend(oemRelation, model))
            .then((updatedOEMRelation:IOEMRelation) => {
              this.findOEMById(oemRelation?.id).ranking = updatedOEMRelation.ranking;
              this.refreshRowGridData();
              dialog.hide();
            })
            .finally(() => {
              dialog.content.inputs.saving = false;
            });
        },
        onCancel: () => dialog.hide()
      },
      size: DialogSize.MD
    });
  };

  private onAddDistributor = (params:IAgButtonDropdownRendererParams):void => {
    const oemRelation:IOEMRelation = params.data;
    const dialog:IDialog = this.messageService.showDialog({
      component: OEMAddDistributorDialogComponent,
      wrapperComponent: false,
      inputs: {
        adding: true,
        entity: 'distributor',
        selectedOEM: oemRelation
      },
      outputs: {
        onSave: (model:IAddDistributorModel) => {
          dialog.content.inputs.saving = true;
          this.bpProfilesOEMDataService.addNewDistributor(this.bpProfilesService.activeProfile?.id, oemRelation.id, model.preferred, model.distributor)
            .then((distributor:IOEMDistributor) => {
              this.onNewDistributorAdded(oemRelation, distributor);
              // close dialog
              dialog.hide();
              // go to detail page after save
              this.goToOEMDetail(oemRelation.id, distributor.id);
            })
            .finally(() => {
              dialog.content.inputs.saving = false;
            });
        },
        onCancel: () => dialog.hide()
      },
      size: DialogSize.MD
    });
  };

  private onSetPreferredDistributor = (params:IAgButtonDropdownRendererParams, index?:number):void => {
    const oemRelation:IOEMRelation = params.data;
    const distributor:IOEMDistributor = oemRelation.distributors[index];

    // set distributor as preferred
    distributor.preferred = true;

    this.bpProfilesOEMDataService.updateDistributor(this.bpProfilesService.activeProfile?.id, oemRelation.id, distributor, 'Marking distributor as preferred')
      .then((updatedDistributor:IOEMDistributor) => {
        this.onUpdatePreferredDistributor(oemRelation, index, updatedDistributor);
      });
  };

  private onDeleteDistributor = (params:IAgButtonDropdownRendererParams, index?:number):void => {
    const oemRelation:IOEMRelation = params.data;
    const distributorIndex:number = oemRelation.tierStatus?.id === OEMTierStatus.DIRECT? (index - 1) : index;
    const distributor:IOEMDistributor = oemRelation.distributors[distributorIndex];

    if (!distributor?.id) {
      this.messageService.showMessageDialog(this.translationService.instant('BPPROFILES.OEM.VALIDATION.CANNOT_DELETE_TITLE'), this.translationService.instant('BPPROFILES.OEM.VALIDATION.CANNOT_DELETE_MESSAGE'), undefined, undefined, undefined, DialogSize.MD);
    } else {
      if (distributor.preferred && oemRelation.tierStatus?.id === OEMTierStatus.DISTRIBUTOR) {
        this.messageService.showMessageDialog(this.translationService.instant('BPPROFILES.OEM.VALIDATION.CANNOT_DELETE_TITLE'), this.translationService.instant('BPPROFILES.OEM.VALIDATION.CANNOT_DELETE_PREFERRED_DISTRIBUTOR'), undefined, undefined, undefined, DialogSize.MD);
      } else {
        const distributorName:string = this.formattingService.formatCompanyCountry(distributor.companyCountry);

        const dialogButtons:IDialogButton[] = [{
          id: BUTTON_DELETE,
          label: 'GA.BUTTONS.DELETE',
          className: 'btn-danger',
          onClick: (updateBusyButtonStatus:DialogBusyButtonFn):void => {
            updateBusyButtonStatus(true);
            this.bpProfilesOEMDataService.deleteDistributor(this.bpProfilesService.activeProfile?.id, oemRelation.id, distributor.id)
              .then(() => {
                this.findOEMById(oemRelation.id).distributors.splice(distributorIndex, 1);
                this.refreshRowGridData();
                dialog.hide();
              })
              .catch(util.noop)
              .finally(() => {
                updateBusyButtonStatus(false);
              });
          }
        }, {
          id: BUTTON_CANCEL,
          label: this.translationService.instant('GA.BUTTONS.DONT_LABEL', { label: 'GA.BUTTONS.DELETE' }),
          className: 'btn-success'
        }];

        const dialog:IDialog = this.messageService.showMessageDialog(
          this.translationService.instant('GA.GENERAL.CONFIRM_DELETE'),
          this.translationService.instant('BPPROFILES.OEM.CONFIRM_DELETE_MESSAGE', { name: distributorName }),
          dialogButtons,
          undefined,
          undefined,
          DialogSize.MD
        );
      }
    }
  };

  private onNewDistributorAdded(oemRelation:IOEMRelation, newDistributor:IOEMDistributor):void {
    if (newDistributor.preferred) {
      // clear all existing preferred distributor in current OEM
      _.forEach(this.findOEMById(oemRelation.id).distributors, (distributor:IOEMDistributor) => {
        distributor.preferred = false;
      });
    }
    // add new distributor to the list of selected OEM and refresh the grid
    this.findOEMById(oemRelation.id).distributors.push(newDistributor);
    this.refreshRowGridData();
    this.goToOEMDetail(oemRelation.id, newDistributor.id);
  }

  private onUpdatePreferredDistributor(oemRelation:IOEMRelation, index:number, updatedDistributor:IOEMDistributor):void {
    _.forEach(this.findOEMById(oemRelation.id).distributors, (distributor:IOEMDistributor) => {
      // change existing distributors to not preferred
      distributor.preferred = false;
    });
    // update current distributor object to the latest and refresh grid row data
    this.findOEMById(oemRelation.id).distributors[index] = updatedDistributor;
    this.refreshRowGridData();
  }

  public ngOnDestroy():void {
    this.onActiveProfileChangeSubscription?.unsubscribe();
  }
}